import { Link } from "react-router-dom";
import { ArrowRight, Award, Compass, HeartHandshake, Lightbulb, ShieldCheck } from "lucide-react";
import { Eyebrow, MaskLine, Reveal, SectionHeading } from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { useContent } from "@/context/ContentContext";

const TEAM_IMG = "https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg";
const ARCH_IMG = "https://images.unsplash.com/photo-1713981910501-d68eca75eb32";

const VALUES = [
  { Icon: ShieldCheck, title: "Integrity", text: "We operate with transparency, responsibility and trust." },
  { Icon: Lightbulb, title: "Innovation", text: "We continuously look for better ways to solve real problems." },
  { Icon: HeartHandshake, title: "Customer First", text: "We design around the needs of customers and businesses." },
  { Icon: Award, title: "Excellence", text: "We aim for quality and continuous improvement in everything we build." },
];

const About = () => {
  const content = useContent();
  return (
  <>
    <section data-testid="about-hero" className="cta-glow pb-24 pt-44 sm:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Eyebrow className="text-gold">About Us</Eyebrow>
        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskLine delay={0.15}>A technology company building</MaskLine>
          <MaskLine delay={0.3}>products that matter.</MaskLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
            Sunkev Enterprises Private Limited is a legally registered private limited
            company in India, focused on technology and digital solutions.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <SectionHeading
            eyebrow="Who We Are"
            title="Sunkev Enterprises Private Limited"
          />
          <Reveal delay={0.2}>
            <p className="mt-7 text-base leading-relaxed text-fog md:text-lg">
              {content.about_story_1 ||
                "Sunkev Enterprises Private Limited is a technology-driven company focused on developing innovative digital products and platforms that create value for businesses, service providers and customers."}
            </p>
            <p className="mt-5 text-base leading-relaxed text-fog md:text-lg">
              {content.about_story_2 ||
                "Through technology, we aim to simplify everyday business interactions, improve accessibility and create scalable solutions for the modern economy."}
            </p>
            <p className="mt-5 text-base leading-relaxed text-fog md:text-lg">
              {content.about_story_3 ? (
                content.about_story_3
              ) : (
                <>
                  Our flagship platform,{" "}
                  <span className="font-semibold text-ink">Craftsi</span>, is a local services
                  marketplace that connects customers with trusted local businesses and service
                  providers.
                </>
              )}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/craftsi"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-xl hover:shadow-navy/25"
            >
              Discover Craftsi
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <img
              src={TEAM_IMG}
              alt="Team collaborating on digital products"
              className="h-[440px] w-full rounded-3xl object-cover shadow-2xl shadow-navy/15"
              loading="lazy"
            />
            <div className="absolute -bottom-8 -left-4 hidden w-56 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:block">
              <img src={ARCH_IMG} alt="Modern corporate architecture" className="h-40 w-full object-cover" loading="lazy" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="brand-band" className="cta-glow py-24">
      <div className="mx-auto flex max-w-7xl flex-col items-center px-5 text-center sm:px-8">
        <Reveal>
          <img
            src="/assets/sunkev-logo.png"
            alt="Sunkev Enterprises — official gold logo"
            className="w-52 sm:w-72"
          />
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.4em] text-white/60">
            Innovating today for a smarter tomorrow
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-mist py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-10 shadow-xl shadow-navy/5">
            <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-navy via-royal to-gold" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.24em] text-navy">
              Company Information
            </h3>
            <dl className="mt-8 divide-y divide-gray-100">
              {[
                ["Company Name", "Sunkev Enterprises Private Limited"],
                ["Legal Entity", "Private Limited Company"],
                ["CIN", "U47912AP2023PTC110487"],
                ["Incorporated", "11 March 2023"],
                ["Country", "India"],
                ["Industry", "Technology & Digital Solutions"],
                ["Flagship Platform", "Craftsi"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col gap-1 py-5 sm:flex-row sm:items-center sm:justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-fog">{k}</dt>
                  <dd className="font-heading text-base font-bold text-ink sm:text-right">
                    {v}
                    {k === "CIN" && (
                      <a
                        href="https://www.mca.gov.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="cin-verify-link-about"
                        className="mt-1 block font-sans text-xs font-semibold text-royal transition-colors duration-300 hover:text-navy"
                      >
                        Verify on mca.gov.in
                      </a>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
        <div className="flex flex-col justify-center">
          <Reveal>
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/30 bg-white shadow-lg shadow-navy/5">
              <Compass className="h-7 w-7 text-gold" strokeWidth={1.5} />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow className="mt-7">Our Vision</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {content.vision_heading || "Building technology that creates meaningful impact."}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-fog md:text-lg">
              {content.vision_text ||
                "To build trusted, innovative and scalable digital platforms that make businesses more accessible, efficient and connected."}
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow="Our Values" title="The principles behind everything we build." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="group h-full rounded-3xl border border-gray-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-xl hover:shadow-navy/10">
                <v.Icon className="h-8 w-8 text-navy transition-colors duration-500 group-hover:text-gold" strokeWidth={1.5} />
                <h3 className="mt-6 font-heading text-xl font-bold text-ink">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fog">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBand />
  </>
  );
};

export default About;
